import { useState } from 'react';
import { Link } from 'react-router';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { describeError } from '@/features/auth/api';
import { useAuth } from '@/features/auth/use-auth';
import { useAuthDialog } from '@/features/auth/use-auth-dialog';
import { fromLocalInput, toLocalInput } from '@/lib/datetime';
import { formatDateTime, formatLira, ticketsFor } from '../format';
import { useJoinTicketEvent, type TicketEventDetail } from '../api';

const statusCopy: Record<string, { label: string; className: string }> = {
  pending: { label: 'İnceleniyor', className: 'border-amber-500/30 bg-amber-500/10 text-amber-300' },
  approved: { label: 'Onaylandı', className: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' },
  rejected: { label: 'Reddedildi', className: 'border-red-500/30 bg-red-500/10 text-red-300' },
};

/**
 * The join flow, in the order the rules ask for it.
 *
 * Signed out: sign in. Signed in without a saved account for THIS site: say so
 * and send them to the profile, because the identifier is not something the form
 * can invent. When both hold, the form opens with the identifier already filled
 * and read-only — it is identity's answer, not a field to retype.
 *
 * Every deposit is its own claim: the history stays on screen, and the form
 * stays available underneath it. A rejection refuses ONE claim, not the member.
 */
export function TicketJoinPanel({ event }: { event: TicketEventDetail }) {
  const { isAuthenticated } = useAuth();
  const auth = useAuthDialog();
  const mine = event.myParticipations;

  if (event.phase === 'upcoming') {
    return (
      <Alert tone="info">
        Etkinlik henüz başlamadı. Başladığında bu sayfadan katılabilirsin.
      </Alert>
    );
  }

  if (event.phase === 'ended') {
    const approved = mine
      .filter((participation) => participation.status === 'approved')
      .reduce((total, participation) => total + participation.tickets, 0);

    return (
      <div className="flex flex-col gap-3">
        <Alert tone="info">
          Etkinliğin katılım süresi doldu.
          {approved > 0 ? ` Onaylanan ${approved} biletin çekilişe dâhil.` : ''}
        </Alert>
        <ParticipationHistory event={event} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex flex-col items-start gap-3">
        <p className="text-sm leading-relaxed text-surface-400">
          Katılmak için giriş yapman gerekiyor. Biletler hesabına yazılır; sonradan
          eşleştirilemeyecek bir başvuru kabul etmiyoruz.
        </p>
        <Button type="button" onClick={() => auth.open('login')}>
          Giriş yap ve katıl
        </Button>
      </div>
    );
  }

  if (!event.myCasinoIdentifier) {
    return (
      <div className="flex flex-col items-start gap-3">
        <Alert tone="info">
          Bu etkinliğe katılmak için önce profiline <strong>{event.siteName}</strong> hesabının
          kullanıcı adını veya ID&apos;sini kaydetmelisin. Yatırımın bu hesapla eşleştirilecek.
        </Alert>
        <Link to="/profile#trusted-sites" className="gf-button gf-button-secondary">
          ID&apos;mi kaydet
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <ParticipationHistory event={event} />

      {mine.length > 0 ? (
        <h3 className="border-t border-surface-800 pt-5 text-sm font-semibold text-surface-200">
          Yeni yatırım bildir
        </h3>
      ) : null}

      <JoinForm key={event.myParticipations.length} event={event} />
    </div>
  );
}

function ParticipationHistory({ event }: { event: TicketEventDetail }) {
  if (event.myParticipations.length === 0) {
    return null;
  }

  return (
    <section aria-labelledby="my-participations">
      <h3 id="my-participations" className="text-sm font-semibold text-surface-200">
        Başvuruların
      </h3>
      <ul className="mt-2 grid gap-2">
        {event.myParticipations.map((participation) => {
          const status = statusCopy[participation.status] ?? statusCopy.pending!;

          return (
            <li
              key={participation.id}
              className="rounded-xl border border-surface-800 bg-surface-950/40 px-4 py-3"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-surface-200">
                  {formatLira(participation.depositAmount)} ·{' '}
                  {formatDateTime(participation.depositedAtUtc)}
                </p>
                <span className={`rounded-full border px-2.5 py-0.5 text-xs ${status.className}`}>
                  {status.label}
                </span>
              </div>

              <p className="mt-1 text-xs text-surface-500">
                {participation.status === 'approved'
                  ? `${participation.tickets} bilet kazanıldı.`
                  : `${participation.tickets} bilet hesaplandı.`}
              </p>

              {participation.status === 'rejected' && participation.rejectionReason ? (
                <p className="mt-2 text-xs leading-relaxed text-red-300">
                  {participation.rejectionReason}
                </p>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function JoinForm({ event }: { event: TicketEventDetail }) {
  const join = useJoinTicketEvent(event.slug);
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const [amount, setAmount] = useState('');
  const [depositedAt, setDepositedAt] = useState(() => toLocalInput(new Date().toISOString()));

  const parsed = Number(amount.replace(',', '.'));
  const tickets = Number.isFinite(parsed)
    ? ticketsFor(parsed, event.depositThreshold, event.ticketsPerThreshold)
    : 0;

  const depositedAtUtc = fromLocalInput(depositedAt);
  const ready = parsed >= event.depositThreshold && depositedAtUtc !== null;

  return (
    <form
      className="ticket-form flex flex-col gap-4"
      onSubmit={(submitEvent) => {
        submitEvent.preventDefault();

        if (!ready || depositedAtUtc === null) {
          return;
        }

        join.mutate(
          { requestId, depositAmount: parsed, depositedAtUtc },
          {
            onSuccess: () => {
              setAmount('');
              setRequestId(crypto.randomUUID());
            },
          },
        );
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Kayıtlı kullanıcı"
          value={event.myCasinoIdentifier ?? ''}
          readOnly
          className="cursor-not-allowed opacity-70 font-mono"
          hint={`${event.siteName} · profilinden geliyor`}
        />
        <Field
          label="Yatırım tutarı (TL)"
          value={amount}
          inputMode="decimal"
          placeholder={formatLira(event.depositThreshold)}
          onChange={(changeEvent) => setAmount(changeEvent.target.value)}
          hint={`En az ${formatLira(event.depositThreshold)} — eşiğin altındaki yatırım bilet kazandırmaz.`}
        />
      </div>

      <Field
        label="Yatırım tarihi ve saati"
        type="datetime-local"
        value={depositedAt}
        onChange={(changeEvent) => setDepositedAt(changeEvent.target.value)}
        hint="Sitedeki yatırım işleminin saati. Etkinlik aralığının içinde olmalı."
      />

      <div className="ticket-total flex flex-wrap items-center justify-between gap-3 rounded-xl border border-brand-500/20 bg-brand-500/[0.06] px-4 py-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-surface-500">Hesaplanan bilet</p>
          <p className="font-display text-2xl font-bold text-brand-400">
            {tickets > 0 ? `${tickets} bilet` : '—'}
          </p>
        </div>
        <p className="max-w-[22rem] text-xs leading-relaxed text-surface-500">
          Biletler başvurun onaylandığında çekilişe eklenir. Her yatırım ayrı bir başvurudur;
          birden çok kez gönderebilirsin.
        </p>
      </div>

      {join.isError ? <Alert tone="error">{describeError(join.error)}</Alert> : null}

      <Button type="submit" loading={join.isPending} disabled={!ready || tickets <= 0}>
        Başvuruyu gönder
      </Button>
    </form>
  );
}
